import {
  type FancyRing,
  FANCY_RINGS,
  drawFancyRing,
  isFancyRing,
} from "./fancyRings";

/**
 * Konkord: frames and small effects drawn over a round picture (avatar,
 * server icon). Everything loops over `t` from 0 to 1, so the GIF made from
 * the frames repeats without a jump. Effects stay near the edge, away from
 * the face in the middle.
 */

export type Ring =
  | FancyRing
  | "none"
  | "simple"
  | "double"
  | "gradient"
  | "glow"
  | "neon"
  | "rainbow"
  | "aurora"
  | "gold"
  | "silver"
  | "dashed"
  | "dotted"
  | "fire"
  | "ice"
  | "flowers"
  | "circuit";

export type Effect =
  | "none"
  | "sparkles"
  | "stars"
  | "hearts"
  | "snow"
  | "shine"
  | "bubbles"
  | "petals"
  | "fireworks"
  | "fireflies"
  | "aura"
  | "lightning"
  | "embers"
  | "confetti"
  | "notes"
  | "orbit"
  | "sweep"
  | "rain"
  | "galaxy";

export type Amount = "low" | "mid" | "high";
export type Speed = "slow" | "normal" | "fast";
export type Thickness = "thin" | "mid" | "thick";
export type Glow = "off" | "soft" | "strong";

export interface Decoration {
  ring: Ring;
  effect: Effect;
  colour: string;
  /** how many particles (or how strong the light); "mid" when missing */
  amount?: Amount;
  /** how fast the loop goes; "normal" when missing */
  speed?: Speed;
  /**
   * frames / effects with colours of their own (gold, fire, confetti...):
   * keep them ("original", the default) or use the chosen ones ("custom")
   */
  palette?: "original" | "custom";
  /** second chosen colour (custom palette); `COLOUR2` when missing */
  colour2?: string;
  /** how thick the frame is; "thin" when missing */
  thickness?: Thickness;
  /** a light around the frame; none when missing */
  glow?: Glow;
  /** the frame runs the other way round */
  reverse?: boolean;
}

/**
 * `own`: has colours of its own, and how many chosen colours replace them
 * (1 or 2) with the custom palette
 */
type Own = { own?: 1 | 2 };

/** `animated`: the frame moves by itself (a still one stays a PNG) */
export const RINGS: ({
  id: Ring;
  label: string;
  animated: boolean;
  /** always uses the two chosen colours */
  duo?: boolean;
} & Own)[] = [
  { id: "none", label: "Nenhuma", animated: false },
  ...FANCY_RINGS.map((ring) => ({ ...ring, duo: true })),
  { id: "simple", label: "Simples", animated: false },
  { id: "double", label: "Dupla fina", animated: false },
  { id: "gradient", label: "Degradê", animated: false, own: 2 },
  { id: "glow", label: "Brilho suave", animated: false },
  { id: "neon", label: "Neon", animated: true },
  { id: "rainbow", label: "Arco-íris", animated: true, own: 2 },
  { id: "aurora", label: "Aurora", animated: true, own: 2 },
  { id: "gold", label: "Ouro", animated: true, own: 1 },
  { id: "silver", label: "Prata", animated: true, own: 1 },
  { id: "dashed", label: "Tracejada", animated: true },
  { id: "dotted", label: "Pontilhada", animated: true },
  { id: "fire", label: "Fogo", animated: true, own: 2 },
  { id: "ice", label: "Gelo", animated: true, own: 2 },
  { id: "flowers", label: "Flores", animated: false, own: 2 },
  { id: "circuit", label: "Circuito", animated: true },
];

export const EFFECTS: ({ id: Effect; label: string } & Own)[] = [
  { id: "none", label: "Nenhum" },
  { id: "sparkles", label: "Brilhos" },
  { id: "stars", label: "Estrelas girando" },
  { id: "hearts", label: "Corações" },
  { id: "snow", label: "Neve", own: 2 },
  { id: "shine", label: "Reflexo", own: 1 },
  { id: "bubbles", label: "Bolhas" },
  { id: "petals", label: "Pétalas" },
  { id: "fireworks", label: "Fogos", own: 2 },
  { id: "fireflies", label: "Vagalumes" },
  { id: "aura", label: "Aura" },
  { id: "lightning", label: "Raios" },
  { id: "embers", label: "Brasas", own: 2 },
  { id: "confetti", label: "Confete", own: 2 },
  { id: "notes", label: "Notas musicais" },
  { id: "orbit", label: "Órbita", own: 2 },
  { id: "sweep", label: "Brilho girando" },
  { id: "rain", label: "Chuva", own: 2 },
  { id: "galaxy", label: "Galáxia", own: 2 },
];

export const AMOUNTS: { id: Amount; label: string; scale: number }[] = [
  { id: "low", label: "Pouca", scale: 0.6 },
  { id: "mid", label: "Média", scale: 1 },
  { id: "high", label: "Muita", scale: 1.6 },
];

/** the old frames were too thick: "thin" is the default now */
export const THICKNESSES: { id: Thickness; label: string; scale: number }[] = [
  { id: "thin", label: "Fina", scale: 0.55 },
  { id: "mid", label: "Média", scale: 0.8 },
  { id: "thick", label: "Grossa", scale: 1.15 },
];

export const GLOWS: { id: Glow; label: string; blur: number }[] = [
  { id: "off", label: "Sem", blur: 0 },
  { id: "soft", label: "Suave", blur: 0.03 },
  { id: "strong", label: "Forte", blur: 0.07 },
];

/** the same 40 frames, shown faster or slower */
export const SPEEDS: { id: Speed; label: string; fps: number }[] = [
  { id: "slow", label: "Lenta", fps: 12 },
  { id: "normal", label: "Normal", fps: 20 },
  { id: "fast", label: "Rápida", fps: 30 },
];

export const COLOURS = [
  "#a78bfa",
  "#f472b6",
  "#22d3ee",
  "#4ade80",
  "#fbbf24",
  "#f87171",
  "#ffffff",
];

/** Konkord: pairs of colours that look good together, one click away */
export const PALETTES: { label: string; colour: string; colour2: string }[] = [
  { label: "Sintonia", colour: "#e879f9", colour2: "#22d3ee" },
  { label: "Pôr do sol", colour: "#fb923c", colour2: "#f43f5e" },
  { label: "Oceano", colour: "#22d3ee", colour2: "#3b82f6" },
  { label: "Floresta", colour: "#4ade80", colour2: "#a3e635" },
  { label: "Realeza", colour: "#fbbf24", colour2: "#a855f7" },
  { label: "Noite", colour: "#818cf8", colour2: "#38bdf8" },
  { label: "Sangue", colour: "#ef4444", colour2: "#fbbf24" },
  { label: "Algodão", colour: "#fda4af", colour2: "#c4b5fd" },
  { label: "Prata", colour: "#e2e8f0", colour2: "#64748b" },
];

/**
 * Konkord: ready-made looks: a frame, an effect and colours that go together
 */
export const LOOKS: { label: string; look: Partial<Decoration> }[] = [
  {
    label: "Neko",
    look: {
      ring: "cat",
      effect: "hearts",
      colour: "#f9a8d4",
      colour2: "#fb7185",
      amount: "low",
      glow: "off",
    },
  },
  {
    label: "Realeza",
    look: {
      ring: "crown",
      effect: "sparkles",
      colour: "#fbbf24",
      colour2: "#ef4444",
      glow: "soft",
    },
  },
  {
    label: "Dragão",
    look: {
      ring: "serpent",
      effect: "embers",
      colour: "#ef4444",
      colour2: "#fbbf24",
      glow: "soft",
    },
  },
  {
    label: "Anjo",
    look: {
      ring: "wings",
      effect: "sparkles",
      colour: "#ffffff",
      colour2: "#bae6fd",
      amount: "low",
      glow: "soft",
    },
  },
  {
    label: "Noite estrelada",
    look: {
      ring: "constellation",
      effect: "galaxy",
      colour: "#a5b4fc",
      colour2: "#fde68a",
      amount: "low",
      glow: "off",
    },
  },
  {
    label: "Cometas",
    look: {
      ring: "comet",
      effect: "none",
      colour: "#22d3ee",
      colour2: "#e879f9",
      glow: "soft",
    },
  },
  {
    label: "Hacker",
    look: {
      ring: "glitch",
      effect: "none",
      colour: "#22d3ee",
      colour2: "#f43f5e",
      glow: "off",
    },
  },
  {
    label: "DJ",
    look: {
      ring: "equalizer",
      effect: "notes",
      colour: "#22d3ee",
      colour2: "#e879f9",
      amount: "low",
      glow: "soft",
      thickness: "mid",
    },
  },
  {
    label: "Mago",
    look: {
      ring: "runes",
      effect: "fireflies",
      colour: "#a78bfa",
      colour2: "#22d3ee",
      amount: "low",
      glow: "soft",
    },
  },
  {
    label: "Joalheria",
    look: {
      ring: "gems",
      effect: "none",
      colour: "#f472b6",
      colour2: "#22d3ee",
      glow: "off",
    },
  },
  {
    label: "Holograma",
    look: {
      ring: "holo",
      effect: "sweep",
      colour: "#a78bfa",
      colour2: "#22d3ee",
      glow: "soft",
    },
  },
  {
    label: "Tóxico",
    look: {
      ring: "plasma",
      effect: "bubbles",
      colour: "#4ade80",
      colour2: "#a3e635",
      amount: "low",
      glow: "soft",
    },
  },
  {
    label: "Cavaleiro",
    look: {
      ring: "chain",
      effect: "none",
      colour: "#cbd5e1",
      colour2: "#64748b",
      glow: "off",
      thickness: "mid",
    },
  },
  {
    label: "Colmeia",
    look: {
      ring: "hive",
      effect: "none",
      colour: "#fbbf24",
      colour2: "#f59e0b",
      glow: "soft",
    },
  },
  {
    label: "Gatsby",
    look: {
      ring: "ornate",
      effect: "shine",
      colour: "#fbbf24",
      colour2: "#fde68a",
      glow: "off",
    },
  },
  {
    label: "Buraco negro",
    look: {
      ring: "vortex",
      effect: "galaxy",
      colour: "#8b5cf6",
      colour2: "#22d3ee",
      amount: "low",
      glow: "off",
    },
  },
];

/** second colour of the custom palette until one is chosen */
export const COLOUR2 = "#f472b6";

/**
 * Konkord: whether the chosen frame / effect has colours of its own (the
 * "Original" / custom choice applies) and whether a second colour is used
 */
export function ownColours(decoration: Decoration) {
  const own = [
    RINGS.find((r) => r.id === decoration.ring)?.own ?? 0,
    EFFECTS.find((e) => e.id === decoration.effect)?.own ?? 0,
  ];
  return {
    any: own.some((n) => n > 0),
    two: own.some((n) => n === 2),
    /** the frame always uses two colours, whatever the palette */
    duo: RINGS.find((r) => r.id === decoration.ring)?.duo ?? false,
  };
}

/** Konkord: one loop of the animation, 2 s at normal speed */
export const LOOP_FRAMES = 40;
export const LOOP_FPS = 20;

/** frames per second of the loop at the chosen speed */
export function loopFps(decoration: Decoration) {
  return SPEEDS.find((s) => s.id === decoration.speed)?.fps ?? LOOP_FPS;
}

/** length of one loop in ms */
export function loopMs(decoration: Decoration) {
  return (LOOP_FRAMES / loopFps(decoration)) * 1000;
}

/** Only a still frame and no effect stays a still picture (full quality) */
export function isAnimated(decoration: Decoration) {
  return (
    decoration.effect !== "none" ||
    (RINGS.find((r) => r.id === decoration.ring)?.animated ?? false)
  );
}

export function isPlain(decoration: Decoration) {
  return decoration.ring === "none" && decoration.effect === "none";
}

/** same positions on every frame and every time */
export function seeded(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let value = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

const TAU = Math.PI * 2;

/** hue (0–360), saturation and lightness (0–100) of a `#rrggbb` colour */
export function hslOf(hex: string): [number, number, number] {
  const n = parseInt(hex.replace("#", "").padEnd(6, "0").slice(0, 6), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h =
    max === r
      ? (g - b) / d + (g < b ? 6 : 0)
      : max === g
        ? (b - r) / d + 2
        : (r - g) / d + 4;
  h *= 60;
  return [h, s * 100, l * 100];
}

/** the colour moved around the wheel / made lighter, as a CSS colour */
export function tone(hex: string, hueShift = 0, lightShift = 0, alpha = 1) {
  const [h, s, l] = hslOf(hex);
  const light = Math.min(97, Math.max(5, l + lightShift));
  return `hsla(${(h + hueShift + 360) % 360}, ${s}%, ${light}%, ${alpha})`;
}

/** `#rrggbb` of a hue / saturation / lightness */
function hexOf(h: number, s: number, l: number) {
  const k = (n: number) => (n + h / 30) % 12;
  const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
  const channel = (n: number) =>
    Math.round(
      255 * (l / 100 - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))),
    )
      .toString(16)
      .padStart(2, "0");
  return `#${channel(0)}${channel(8)}${channel(4)}`;
}

/** a colour between two (0 = first, 1 = second), going the short way round */
export function blend(first: string, second: string, k: number) {
  const [h1, s1, l1] = hslOf(first);
  const [h2, s2, l2] = hslOf(second);
  // a grey has no hue: take the other one's
  const from = s1 < 1 ? h2 : h1;
  const to = s2 < 1 ? h1 : h2;
  let dh = to - from;
  if (dh > 180) dh -= 360;
  if (dh < -180) dh += 360;
  return hexOf(
    (from + dh * k + 360) % 360,
    s1 + (s2 - s1) * k,
    l1 + (l2 - l1) * k,
  );
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/** 0 over the face in the middle, 1 near the edge */
function edgeFade(size: number, x: number, y: number) {
  const centre = size / 2;
  const distance = Math.hypot(x - centre, y - centre) / centre;
  return clamp01((distance - 0.55) / 0.17);
}

/**
 * A point on the round border band: `side` -1 left / 1 right, `y` from the
 * top, `inset` 1 = right on the edge, 0.75 = a quarter of the way in
 */
function onBorder(size: number, side: number, y: number, inset: number) {
  const centre = size / 2;
  const dy = y - centre;
  const half = Math.sqrt(Math.max(0, centre * centre - dy * dy));
  return centre + side * half * inset;
}

/** a scratch canvas to paint a layer and fade it out towards the middle */
let scratch: HTMLCanvasElement | undefined;
function edgeLayer(
  context: CanvasRenderingContext2D,
  size: number,
  inner: number,
  paint: (layer: CanvasRenderingContext2D) => void,
  composite: GlobalCompositeOperation = "source-over",
) {
  scratch ??= document.createElement("canvas");
  if (scratch.width !== size) scratch.width = scratch.height = size;
  const layer = scratch.getContext("2d")!;
  layer.globalCompositeOperation = "source-over";
  layer.clearRect(0, 0, size, size);
  paint(layer);
  const centre = size / 2;
  const mask = layer.createRadialGradient(
    centre,
    centre,
    centre * inner,
    centre,
    centre,
    centre * Math.min(1, inner + 0.18),
  );
  mask.addColorStop(0, "rgba(0, 0, 0, 0)");
  mask.addColorStop(1, "rgba(0, 0, 0, 1)");
  layer.globalCompositeOperation = "destination-in";
  layer.fillStyle = mask;
  layer.fillRect(0, 0, size, size);
  context.save();
  context.globalCompositeOperation = composite;
  context.drawImage(scratch, 0, 0);
  context.restore();
}

export function sparkle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  colour: string,
) {
  if (radius <= 0.3) return;
  context.save();
  context.shadowColor = colour;
  context.shadowBlur = radius * 1.5;
  context.fillStyle = "#ffffff";
  context.beginPath();
  context.moveTo(x, y - radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.quadraticCurveTo(x, y, x, y + radius);
  context.quadraticCurveTo(x, y, x - radius, y);
  context.quadraticCurveTo(x, y, x, y - radius);
  context.fill();
  context.restore();
}

function heart(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  colour: string,
) {
  context.save();
  context.fillStyle = colour;
  context.beginPath();
  context.moveTo(x, y + size * 0.35);
  context.bezierCurveTo(
    x - size,
    y - size * 0.3,
    x - size * 0.4,
    y - size,
    x,
    y - size * 0.35,
  );
  context.bezierCurveTo(
    x + size * 0.4,
    y - size,
    x + size,
    y - size * 0.3,
    x,
    y + size * 0.35,
  );
  context.fill();
  context.restore();
}

/** a petal or a leaf pointing along +x, `length` long */
function leafPath(context: CanvasRenderingContext2D, length: number) {
  const width = length * 0.42;
  context.beginPath();
  context.moveTo(0, 0);
  context.quadraticCurveTo(length * 0.45, -width, length, 0);
  context.quadraticCurveTo(length * 0.45, width, 0, 0);
}

/** a small five-petal flower */
function flower(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  colour: string,
  hueShift: number,
  turn: number,
) {
  context.save();
  context.translate(x, y);
  context.rotate(turn);
  for (let i = 0; i < 5; i++) {
    context.save();
    context.rotate((i * TAU) / 5);
    const gradient = context.createLinearGradient(0, 0, radius, 0);
    gradient.addColorStop(0, tone(colour, hueShift, -12));
    gradient.addColorStop(1, tone(colour, hueShift, 14));
    context.fillStyle = gradient;
    leafPath(context, radius);
    context.fill();
    context.restore();
  }
  context.fillStyle = "#fde68a";
  context.beginPath();
  context.arc(0, 0, radius * 0.26, 0, TAU);
  context.fill();
  context.fillStyle = "#f59e0b";
  context.beginPath();
  context.arc(0, 0, radius * 0.13, 0, TAU);
  context.fill();
  context.restore();
}

/** a musical note (one or two heads), `size` tall */
function note(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  double: boolean,
) {
  const head = size * 0.22;
  const drawHead = (hx: number, hy: number) => {
    context.save();
    context.translate(hx, hy);
    context.rotate(-0.4);
    context.beginPath();
    context.ellipse(0, 0, head * 1.25, head * 0.9, 0, 0, TAU);
    context.fill();
    context.restore();
  };
  context.lineWidth = size * 0.08;
  context.lineCap = "round";
  if (double) {
    const gap = size * 0.55;
    drawHead(x, y + size * 0.4);
    drawHead(x + gap, y + size * 0.3);
    context.beginPath();
    context.moveTo(x + head, y + size * 0.4);
    context.lineTo(x + head, y - size * 0.45);
    context.moveTo(x + gap + head, y + size * 0.3);
    context.lineTo(x + gap + head, y - size * 0.55);
    context.stroke();
    context.lineWidth = size * 0.16;
    context.beginPath();
    context.moveTo(x + head, y - size * 0.45);
    context.lineTo(x + gap + head, y - size * 0.55);
    context.stroke();
  } else {
    drawHead(x, y + size * 0.4);
    context.beginPath();
    context.moveTo(x + head, y + size * 0.4);
    context.lineTo(x + head, y - size * 0.5);
    context.quadraticCurveTo(
      x + head + size * 0.4,
      y - size * 0.3,
      x + head + size * 0.3,
      y,
    );
    context.stroke();
  }
}

/**
 * one flame tongue, from the edge (`base` radius) towards the middle; the
 * tip leans sideways by `bend` (-1 to 1)
 */
function flame(
  context: CanvasRenderingContext2D,
  centre: number,
  angle: number,
  base: number,
  height: number,
  width: number,
  bend: number,
  colours: [string, string, string],
) {
  context.save();
  context.translate(centre, centre);
  context.rotate(angle);
  // x axis now points out to the edge
  const tipX = base - height;
  const tipY = bend * width * 1.2;
  const gradient = context.createLinearGradient(base, 0, tipX, 0);
  gradient.addColorStop(0, colours[0]);
  gradient.addColorStop(0.5, colours[1]);
  gradient.addColorStop(1, colours[2]);
  context.fillStyle = gradient;
  context.beginPath();
  context.moveTo(base, -width);
  context.bezierCurveTo(
    base - height * 0.35,
    -width * 1.15,
    base - height * 0.7,
    tipY - width * 0.45,
    tipX,
    tipY,
  );
  context.bezierCurveTo(
    base - height * 0.7,
    tipY + width * 0.45,
    base - height * 0.35,
    width * 1.15,
    base,
    width,
  );
  context.closePath();
  context.fill();
  context.restore();
}

/** a jagged electric line along the edge, from angle `from` to `to` */
function bolt(
  context: CanvasRenderingContext2D,
  centre: number,
  radius: number,
  from: number,
  to: number,
  jitter: number,
  random: () => number,
) {
  const steps = 9;
  context.beginPath();
  for (let i = 0; i <= steps; i++) {
    const angle = from + ((to - from) * i) / steps;
    const r =
      radius + (i === 0 || i === steps ? 0 : (random() - 0.5) * 2 * jitter);
    const x = centre + Math.cos(angle) * r;
    const y = centre + Math.sin(angle) * r;
    if (i === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.stroke();
}

function drawRing(
  context: CanvasRenderingContext2D,
  size: number,
  time: number,
  decoration: Decoration,
) {
  const t = decoration.reverse ? (1 - time) % 1 : time;
  const k =
    THICKNESSES.find((thickness) => thickness.id === decoration.thickness)
      ?.scale ?? THICKNESSES[0].scale;
  if (isFancyRing(decoration.ring)) {
    drawFancyRing(context, size, t, decoration, k);
    return;
  }
  const centre = size / 2;
  const colour = decoration.colour;
  // Konkord: the chosen colours instead of the frame's own
  const custom = decoration.palette === "custom";
  const colour2 = decoration.colour2 ?? COLOUR2;
  const middle = blend(colour, colour2, 0.5);
  const width = size * 0.06 * k;
  const radius = centre - width / 2;
  const circle = (r = radius) => {
    context.beginPath();
    context.arc(centre, centre, r, 0, TAU);
  };
  context.save();
  context.lineWidth = width;

  switch (decoration.ring) {
    case "simple": {
      // Konkord: half the old thickness, still a full pixel at 32 px
      const thin = size * 0.03 * Math.max(k, 0.7);
      context.lineWidth = thin;
      context.strokeStyle = colour;
      circle(centre - thin / 2);
      context.stroke();
      break;
    }

    case "double": {
      const outer = size * 0.016 * Math.max(k, 0.7);
      const inner = size * 0.009 * Math.max(k, 0.7);
      context.strokeStyle = colour;
      context.lineWidth = outer;
      circle(centre - outer / 2);
      context.stroke();
      context.strokeStyle = tone(colour, 0, 12);
      context.lineWidth = inner;
      circle(centre - size * 0.042);
      context.stroke();
      break;
    }

    case "gradient": {
      const thick = size * 0.045 * k;
      const gradient = context.createLinearGradient(0, 0, size, size);
      if (custom) {
        gradient.addColorStop(0, colour);
        gradient.addColorStop(0.5, middle);
        gradient.addColorStop(1, colour2);
      } else {
        gradient.addColorStop(0, tone(colour, -30, 14));
        gradient.addColorStop(0.5, colour);
        gradient.addColorStop(1, tone(colour, 40, -10));
      }
      context.lineWidth = thick;
      context.strokeStyle = gradient;
      circle(centre - thick / 2);
      context.stroke();
      // a fine light line on the inner side, like a bevel
      context.lineWidth = size * 0.005;
      context.strokeStyle = "rgba(255, 255, 255, 0.35)";
      circle(centre - thick + size * 0.004);
      context.stroke();
      break;
    }

    case "glow": {
      const halo = context.createRadialGradient(
        centre,
        centre,
        centre * 0.78,
        centre,
        centre,
        centre,
      );
      halo.addColorStop(0, tone(colour, 0, 0, 0));
      halo.addColorStop(0.7, tone(colour, 0, 5, 0.22));
      halo.addColorStop(1, tone(colour, 0, 10, 0.6));
      context.fillStyle = halo;
      context.fillRect(0, 0, size, size);
      const line = size * 0.012;
      context.lineWidth = line;
      context.strokeStyle = colour;
      context.shadowColor = colour;
      context.shadowBlur = size * 0.03;
      circle(centre - line / 2);
      context.stroke();
      context.shadowBlur = 0;
      context.lineWidth = size * 0.004;
      context.strokeStyle = "rgba(255, 255, 255, 0.8)";
      circle(centre - line / 2);
      context.stroke();
      break;
    }

    case "neon": {
      const pulse = 0.5 + 0.5 * Math.sin(TAU * t);
      context.strokeStyle = colour;
      context.shadowColor = colour;
      context.shadowBlur = size * (0.02 + 0.05 * pulse);
      circle();
      context.stroke();
      context.shadowBlur = 0;
      context.lineWidth = width * 0.3;
      context.strokeStyle = `rgba(255, 255, 255, ${0.5 + 0.4 * pulse})`;
      circle();
      context.stroke();
      break;
    }

    case "rainbow": {
      const gradient = context.createConicGradient(TAU * t, centre, centre);
      if (custom) {
        // round the two colours and what lies between them
        [colour, middle, colour2, middle, colour].forEach((stop, i) =>
          gradient.addColorStop(i / 4, stop),
        );
      } else {
        for (let i = 0; i <= 6; i++)
          gradient.addColorStop(i / 6, `hsl(${i * 60}, 90%, 60%)`);
      }
      context.strokeStyle = gradient;
      circle();
      context.stroke();
      break;
    }

    case "aurora": {
      const thick = size * 0.05 * k;
      // colours along the band: the chosen colour moved round the wheel,
      // or the two chosen ones
      const band = (
        turn: number,
        shifts: number[],
        picks: string[],
        alpha: number,
      ) => {
        const gradient = context.createConicGradient(turn, centre, centre);
        const stops = custom
          ? picks.map((pick) => tone(pick, 0, 12, alpha))
          : shifts.map((shift) => tone(colour, shift, 12, alpha));
        stops.forEach((stop, i) =>
          gradient.addColorStop(i / (stops.length - 1), stop),
        );
        return gradient;
      };
      context.lineWidth = thick;
      context.shadowColor = colour;
      context.shadowBlur = size * 0.03;
      context.strokeStyle = band(
        TAU * t,
        [0, 70, -40, 110, 0],
        [colour, colour2, middle, colour2, colour],
        1,
      );
      circle(centre - thick / 2);
      context.stroke();
      context.shadowBlur = 0;
      // a second veil turning the other way makes the colours drift
      context.strokeStyle = band(
        -TAU * t + 1,
        [0, -80, 50, 0],
        [colour2, middle, colour, colour2],
        0.55,
      );
      context.lineWidth = thick * 0.55;
      circle(centre - thick * 0.6);
      context.stroke();
      break;
    }

    case "gold":
    case "silver": {
      // custom: the same metal shading made from the chosen colour
      const stops = custom
        ? [28, -18, 18, -32, 6].map((light) => tone(colour, 0, light))
        : decoration.ring === "gold"
          ? ["#fde68a", "#d97706", "#fcd34d", "#92400e", "#fbbf24"]
          : ["#f8fafc", "#94a3b8", "#e2e8f0", "#475569", "#cbd5e1"];
      const gradient = context.createLinearGradient(0, 0, size, size);
      [0, 0.3, 0.55, 0.8, 1].forEach((at, i) =>
        gradient.addColorStop(at, stops[i]),
      );
      context.strokeStyle = gradient;
      circle();
      context.stroke();
      // a glint running around it
      context.lineWidth = width * 0.5;
      context.lineCap = "round";
      context.strokeStyle = "rgba(255, 255, 255, 0.9)";
      context.shadowColor = custom
        ? tone(colour, 0, 30)
        : decoration.ring === "gold"
          ? "#fff7d6"
          : "#e0f2fe";
      context.shadowBlur = width;
      context.beginPath();
      context.arc(centre, centre, radius, TAU * t, TAU * t + 0.5);
      context.stroke();
      break;
    }

    case "dashed": {
      const period = (TAU * radius) / 16;
      context.strokeStyle = colour;
      context.globalAlpha = 0.35;
      circle();
      context.stroke();
      context.globalAlpha = 1;
      context.setLineDash([period * 0.6, period * 0.4]);
      // four dash lengths per loop, so the end matches the start
      context.lineDashOffset = -t * period * 4;
      circle();
      context.stroke();
      break;
    }

    case "dotted": {
      const count = 28;
      const r = centre - size * 0.028;
      const dot = size * 0.016;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * TAU - Math.PI / 2;
        // a wave of light runs around once per loop
        let gap = Math.abs(i / count - t) % 1;
        gap = Math.min(gap, 1 - gap);
        const glow = Math.exp(-(gap * gap) / 0.004);
        const x = centre + Math.cos(angle) * r;
        const y = centre + Math.sin(angle) * r;
        context.fillStyle = tone(colour, 0, 18 * glow, 0.75 + 0.25 * glow);
        context.beginPath();
        context.arc(x, y, dot * (1 + 0.35 * glow), 0, TAU);
        context.fill();
        if (glow > 0.2) {
          context.fillStyle = `rgba(255, 255, 255, ${0.8 * glow})`;
          context.beginPath();
          context.arc(x, y, dot * 0.45, 0, TAU);
          context.fill();
        }
      }
      break;
    }

    case "fire": {
      // embers glowing under the flames
      const base = context.createRadialGradient(
        centre,
        centre,
        centre * (1 - 0.18 * k),
        centre,
        centre,
        centre,
      );
      base.addColorStop(
        0,
        custom ? tone(colour, 0, 0, 0) : "rgba(220, 38, 38, 0)",
      );
      base.addColorStop(
        0.55,
        custom ? tone(colour, 0, -5, 0.45) : "rgba(234, 88, 12, 0.45)",
      );
      base.addColorStop(
        1,
        custom ? tone(colour, 0, -25, 0.95) : "rgba(153, 27, 27, 0.95)",
      );
      context.fillStyle = base;
      context.fillRect(0, 0, size, size);
      const random = seeded(31);
      // red outside, orange, then a yellow-white heart
      const layers: {
        count: number;
        height: [number, number];
        width: number;
        colours: [string, string, string];
      }[] = [
        {
          count: 18,
          height: [0.07 * k, 0.07 * k],
          width: 0.05,
          colours: custom
            ? [
                tone(colour, 0, -15, 0.9),
                tone(colour, 0, 0, 0.75),
                tone(colour, 0, 10, 0),
              ]
            : [
                "rgba(185, 28, 28, 0.9)",
                "rgba(234, 88, 12, 0.75)",
                "rgba(249, 115, 22, 0)",
              ],
        },
        {
          count: 18,
          height: [0.05 * k, 0.05 * k],
          width: 0.035,
          colours: custom
            ? [
                tone(middle, 0, 0, 0.95),
                tone(colour2, 0, 5, 0.8),
                tone(colour2, 0, 15, 0),
              ]
            : [
                "rgba(249, 115, 22, 0.95)",
                "rgba(251, 191, 36, 0.8)",
                "rgba(253, 224, 71, 0)",
              ],
        },
        {
          count: 14,
          height: [0.025 * k, 0.03 * k],
          width: 0.02,
          colours: custom
            ? [
                tone(colour2, 0, 35, 0.95),
                tone(colour2, 0, 20, 0.7),
                "rgba(255, 255, 255, 0)",
              ]
            : [
                "rgba(254, 249, 195, 0.95)",
                "rgba(253, 224, 71, 0.7)",
                "rgba(255, 255, 255, 0)",
              ],
        },
      ];
      context.save();
      context.shadowColor = custom
        ? tone(colour, 0, 0, 0.6)
        : "rgba(249, 115, 22, 0.6)";
      context.shadowBlur = size * 0.015;
      layers.forEach((layer, n) => {
        for (let i = 0; i < layer.count; i++) {
          const angle = ((i + n * 0.37 + random() * 0.4) / layer.count) * TAU;
          const speed = 1 + Math.floor(random() * 3);
          const phase = random();
          const lick = 0.5 + 0.5 * Math.sin(TAU * (speed * t + phase));
          flame(
            context,
            centre,
            angle,
            centre + size * 0.01,
            size * (layer.height[0] + layer.height[1] * lick),
            size * layer.width * (0.8 + 0.4 * random()),
            0.6 * Math.sin(TAU * (t * speed + phase + 0.25)),
            layer.colours,
          );
        }
      });
      context.restore();
      break;
    }

    case "ice": {
      const thick = size * 0.04 * k;
      const gradient = context.createLinearGradient(0, 0, size, size);
      (custom
        ? [
            tone(colour, 0, 25),
            colour,
            tone(colour2, 0, 25),
            colour2,
            tone(middle, 0, 15),
          ]
        : ["#f0f9ff", "#7dd3fc", "#e0f2fe", "#38bdf8", "#bae6fd"]
      ).forEach((stop, i) =>
        gradient.addColorStop([0, 0.3, 0.5, 0.75, 1][i], stop),
      );
      context.lineWidth = thick;
      context.strokeStyle = gradient;
      circle(centre - thick / 2);
      context.stroke();
      // crystal shards pointing in
      const random = seeded(17);
      const tips: [number, number][] = [];
      const count = 16;
      for (let i = 0; i < count; i++) {
        const angle = ((i + random() * 0.4) / count) * TAU;
        const length = size * (0.045 + random() * 0.06) * Math.max(k, 0.7);
        const spread = 0.04 + random() * 0.025;
        const r0 = centre - thick * 0.7;
        const tipR = r0 - length;
        const point = (r: number, a: number): [number, number] => [
          centre + Math.cos(a) * r,
          centre + Math.sin(a) * r,
        ];
        const [ax, ay] = point(r0, angle - spread);
        const [bx, by] = point(tipR, angle);
        const [cx, cy] = point(r0, angle + spread);
        const shard = context.createLinearGradient(ax, ay, bx, by);
        shard.addColorStop(
          0,
          custom ? tone(colour, 0, 30, 0.95) : "rgba(224, 242, 254, 0.95)",
        );
        shard.addColorStop(
          1,
          custom ? tone(colour2, 0, 10, 0.25) : "rgba(125, 211, 252, 0.25)",
        );
        context.fillStyle = shard;
        context.strokeStyle = "rgba(255, 255, 255, 0.7)";
        context.lineWidth = size * 0.0025;
        context.beginPath();
        context.moveTo(ax, ay);
        context.lineTo(bx, by);
        context.lineTo(cx, cy);
        context.closePath();
        context.fill();
        context.stroke();
        tips.push([bx, by]);
      }
      // a glint going around and a few shard tips twinkling
      const glint = TAU * t;
      sparkle(
        context,
        centre + Math.cos(glint) * (centre - thick / 2),
        centre + Math.sin(glint) * (centre - thick / 2),
        size * 0.04,
        custom ? colour2 : "#7dd3fc",
      );
      tips.forEach(([x, y], i) => {
        if (i % 4) return;
        const glow = Math.max(0, Math.sin(TAU * (t + i / count))) ** 4;
        sparkle(context, x, y, size * 0.03 * glow, custom ? colour : "#bae6fd");
      });
      break;
    }

    case "flowers": {
      const r = centre - size * 0.04 * Math.max(k, 0.75);
      context.lineWidth = size * 0.01;
      context.strokeStyle = "#4d7c0f";
      circle(r);
      context.stroke();
      const random = seeded(23);
      const leaves = 30;
      for (let i = 0; i < leaves; i++) {
        const angle = (i / leaves) * TAU;
        const out = i % 2 ? 1 : -1;
        context.save();
        context.translate(
          centre + Math.cos(angle) * r,
          centre + Math.sin(angle) * r,
        );
        // along the vine, tilted a little in or out
        context.rotate(angle + Math.PI / 2 + out * 0.5);
        const length = size * (0.035 + random() * 0.015) * Math.max(k, 0.75);
        const gradient = context.createLinearGradient(0, 0, length, 0);
        gradient.addColorStop(0, "#3f6212");
        gradient.addColorStop(1, "#84cc16");
        context.fillStyle = gradient;
        leafPath(context, length);
        context.fill();
        context.restore();
      }
      const flowers = 8;
      for (let i = 0; i < flowers; i++) {
        const angle = (i / flowers) * TAU + Math.PI / 2;
        flower(
          context,
          centre + Math.cos(angle) * r,
          centre + Math.sin(angle) * r,
          size * (i % 2 ? 0.04 : 0.052) * Math.max(k, 0.75),
          custom && i % 2 ? colour2 : colour,
          !custom && i % 2 ? 30 : 0,
          random() * TAU,
        );
      }
      break;
    }

    case "circuit": {
      const r = centre - size * 0.012;
      context.lineWidth = size * 0.012 * Math.max(k, 0.7);
      context.strokeStyle = colour;
      circle(r);
      context.stroke();
      const random = seeded(41);
      const count = 14;
      context.lineWidth = size * 0.006;
      context.lineCap = "round";
      context.lineJoin = "round";
      for (let i = 0; i < count; i++) {
        const angle = ((i + random() * 0.5) / count) * TAU;
        const turn = (random() < 0.5 ? -1 : 1) * (0.08 + random() * 0.12);
        const r1 = r - size * (0.03 + random() * 0.04) * Math.max(k, 0.7);
        const r2 = r1 - size * (0.02 + random() * 0.03) * Math.max(k, 0.7);
        const at = (rr: number, a: number): [number, number] => [
          centre + Math.cos(a) * rr,
          centre + Math.sin(a) * rr,
        ];
        const points = [
          at(r, angle),
          at(r1, angle),
          at(r1, angle + turn),
          at(r2, angle + turn),
        ];
        context.strokeStyle = tone(colour, 0, 0, 0.85);
        context.beginPath();
        points.forEach(([x, y], n) =>
          n ? context.lineTo(x, y) : context.moveTo(x, y),
        );
        context.stroke();
        const [px, py] = points[3];
        context.fillStyle = "rgba(0, 0, 0, 0.35)";
        context.beginPath();
        context.arc(px, py, size * 0.009, 0, TAU);
        context.fill();
        context.stroke();
        // a pulse running from the ring to the pad
        const progress = (t + random()) % 1;
        if (progress < 0.6) {
          const along = progress / 0.6;
          const lengths = [0, 1, 2].map((n) =>
            Math.hypot(
              points[n + 1][0] - points[n][0],
              points[n + 1][1] - points[n][1],
            ),
          );
          let left = along * lengths.reduce((a, b) => a + b, 0);
          let n = 0;
          while (n < 2 && left > lengths[n]) left -= lengths[n++];
          const k = lengths[n] ? left / lengths[n] : 0;
          const x = points[n][0] + (points[n + 1][0] - points[n][0]) * k;
          const y = points[n][1] + (points[n + 1][1] - points[n][1]) * k;
          context.save();
          context.fillStyle = "#ffffff";
          context.shadowColor = colour;
          context.shadowBlur = size * 0.02;
          context.globalAlpha = Math.sin(Math.PI * along);
          context.beginPath();
          context.arc(x, y, size * 0.008, 0, TAU);
          context.fill();
          context.restore();
        }
      }
      break;
    }
  }
  context.restore();
}

function drawEffect(
  context: CanvasRenderingContext2D,
  size: number,
  t: number,
  decoration: Decoration,
) {
  const centre = size / 2;
  const colour = decoration.colour;
  // Konkord: the chosen colours instead of the effect's own
  const custom = decoration.palette === "custom";
  const colour2 = decoration.colour2 ?? COLOUR2;
  const middle = blend(colour, colour2, 0.5);
  const random = seeded(7);
  const scale = AMOUNTS.find((a) => a.id === decoration.amount)?.scale ?? 1;
  const many = (count: number) => Math.max(1, Math.round(count * scale));

  switch (decoration.effect) {
    case "sparkles":
      for (let i = 0; i < many(7); i++) {
        const angle = random() * TAU;
        const distance = centre * (0.55 + random() * 0.25);
        const phase = random();
        const glow = Math.max(0, Math.sin(TAU * (t + phase))) ** 3;
        sparkle(
          context,
          centre + Math.cos(angle) * distance,
          centre + Math.sin(angle) * distance,
          size * 0.05 * glow,
          colour,
        );
      }
      break;

    case "stars": {
      const distance =
        decoration.ring === "none" ? centre * 0.86 : centre - size * 0.03;
      const count = many(3);
      for (let i = 0; i < count; i++) {
        const angle = TAU * t + (i * TAU) / count;
        const glow = 0.75 + 0.25 * Math.sin(TAU * (2 * t + i / count));
        sparkle(
          context,
          centre + Math.cos(angle) * distance,
          centre + Math.sin(angle) * distance,
          size * 0.055 * glow,
          colour,
        );
      }
      break;
    }

    case "hearts":
      for (let i = 0; i < many(6); i++) {
        const left = i % 2 === 0;
        const x = size * (left ? 0.14 + random() * 0.1 : 0.76 + random() * 0.1);
        const progress = (t + random()) % 1;
        context.save();
        context.globalAlpha = Math.sin(Math.PI * progress);
        heart(
          context,
          x,
          size * (0.88 - 0.55 * progress),
          size * 0.045,
          colour,
        );
        context.restore();
      }
      break;

    case "snow":
      context.save();
      for (let i = 0; i < many(16); i++) {
        const x0 = random() * size;
        const progress = (t + random()) % 1;
        const radius = size * (0.008 + random() * 0.006);
        const x = x0 + Math.sin(TAU * t + i) * size * 0.02;
        const y = progress * size * 1.1 - size * 0.05;
        const fade = edgeFade(size, x, y);
        if (fade <= 0) continue;
        context.fillStyle = custom
          ? tone(i % 2 ? colour2 : colour, 0, 15, 0.85 * fade)
          : `rgba(255, 255, 255, ${0.85 * fade})`;
        context.beginPath();
        context.arc(x, y, radius, 0, TAU);
        context.fill();
      }
      context.restore();
      break;

    case "shine": {
      // one sweep at the start of each loop, still the rest of the time
      const sweep = t / 0.4;
      if (sweep >= 1) break;
      const at = -0.3 + 1.6 * sweep;
      const gradient = context.createLinearGradient(0, 0, size, size);
      const strength = 0.35 * Math.min(1.4, scale);
      const stop = (offset: number, alpha: number) =>
        gradient.addColorStop(
          clamp01(offset),
          custom ? tone(colour, 0, 25, alpha) : `rgba(255, 255, 255, ${alpha})`,
        );
      stop(at - 0.12, 0);
      stop(at, strength);
      stop(at + 0.12, 0);
      context.save();
      context.fillStyle = gradient;
      context.fillRect(0, 0, size, size);
      context.restore();
      break;
    }

    case "bubbles":
      for (let i = 0; i < many(10); i++) {
        const side = i % 2 ? 1 : -1;
        const progress = (t + random()) % 1;
        const inset = 0.74 + random() * 0.16;
        const radius = size * (0.018 + random() * 0.02);
        const y = size * (1.02 - 1.04 * progress);
        const x =
          onBorder(size, side, y, inset) +
          Math.sin(TAU * (2 * t + random())) * size * 0.008;
        const alpha = Math.sin(Math.PI * progress);
        context.save();
        context.globalAlpha = alpha;
        context.fillStyle = tone(colour, 0, 25, 0.16);
        context.strokeStyle = tone(colour, 0, 28, 0.85);
        context.lineWidth = size * 0.004;
        context.beginPath();
        context.arc(x, y, radius, 0, TAU);
        context.fill();
        context.stroke();
        context.strokeStyle = "rgba(255, 255, 255, 0.85)";
        context.beginPath();
        context.arc(x, y, radius * 0.62, Math.PI * 1.1, Math.PI * 1.45);
        context.stroke();
        context.restore();
      }
      break;

    case "petals":
      for (let i = 0; i < many(9); i++) {
        const side = i % 2 ? 1 : -1;
        const progress = (t + random()) % 1;
        const inset = 0.72 + random() * 0.2;
        const y = size * (-0.02 + 1.04 * progress);
        const x =
          onBorder(size, side, y, inset) +
          Math.sin(TAU * (t + random())) * size * 0.02;
        const spin = random() < 0.5 ? 1 : -1;
        const phase = random();
        context.save();
        context.globalAlpha = Math.sin(Math.PI * progress);
        context.translate(x, y);
        context.rotate(TAU * (spin * t + phase));
        // flutter: the petal turns over as it falls
        context.scale(
          1,
          0.35 + 0.65 * Math.abs(Math.cos(TAU * (2 * t + phase))),
        );
        const length = size * 0.065;
        const gradient = context.createLinearGradient(0, 0, length, 0);
        gradient.addColorStop(0, tone(colour, -8, -8));
        gradient.addColorStop(1, tone(colour, 6, 16));
        context.fillStyle = gradient;
        context.translate(-length / 2, 0);
        leafPath(context, length);
        context.fill();
        context.restore();
      }
      break;

    case "fireworks": {
      const bursts = many(3);
      for (let i = 0; i < bursts; i++) {
        const angle = ((i + random() * 0.5) / bursts) * TAU - Math.PI / 2;
        const distance = centre * (0.76 + random() * 0.08);
        const x = centre + Math.cos(angle) * distance;
        const y = centre + Math.sin(angle) * distance;
        const progress = (t + i / bursts) % 1;
        if (progress > 0.6) continue;
        const open = 1 - (1 - progress / 0.6) ** 3;
        const fade = (1 - progress / 0.6) ** 1.3;
        const reach = size * 0.11 * open;
        const hue = custom
          ? tone([colour, colour2, middle][i % 3], 0, 12)
          : tone(colour, i * 55, 12);
        context.save();
        context.globalCompositeOperation = "lighter";
        context.strokeStyle = hue;
        context.fillStyle = "#ffffff";
        context.lineCap = "round";
        context.lineWidth = size * 0.006;
        context.globalAlpha = fade;
        const sparks = 14;
        for (let s = 0; s < sparks; s++) {
          const a = (s / sparks) * TAU + i;
          const cos = Math.cos(a);
          const sin = Math.sin(a);
          context.beginPath();
          context.moveTo(x + cos * reach * 0.55, y + sin * reach * 0.55);
          // a little gravity on the tips
          context.lineTo(
            x + cos * reach,
            y + sin * reach + reach * 0.12 * open,
          );
          context.stroke();
          context.beginPath();
          context.arc(
            x + cos * reach,
            y + sin * reach + reach * 0.12 * open,
            size * 0.005,
            0,
            TAU,
          );
          context.fill();
        }
        context.restore();
      }
      break;
    }

    case "fireflies":
      for (let i = 0; i < many(12); i++) {
        const a0 = random() * TAU;
        const angle = a0 + 0.3 * Math.sin(TAU * (t + random()));
        const distance =
          centre * (0.8 + 0.1 * Math.sin(TAU * (2 * t + random())));
        const x = centre + Math.cos(angle) * distance;
        const y = centre + Math.sin(angle) * distance;
        const speed = 1 + Math.floor(random() * 2);
        const blink =
          0.15 +
          0.85 * Math.max(0, Math.sin(TAU * (speed * t + random()))) ** 2;
        const radius = size * 0.035;
        const glow = context.createRadialGradient(x, y, 0, x, y, radius);
        glow.addColorStop(0, `rgba(255, 251, 230, ${blink})`);
        glow.addColorStop(0.25, tone(colour, 0, 20, 0.8 * blink));
        glow.addColorStop(1, tone(colour, 0, 10, 0));
        context.fillStyle = glow;
        context.beginPath();
        context.arc(x, y, radius, 0, TAU);
        context.fill();
      }
      break;

    case "aura": {
      const pulse = 0.5 + 0.5 * Math.sin(TAU * t);
      const strength = Math.min(1, 0.5 * scale);
      edgeLayer(context, size, 0.66 + 0.08 * (1 - pulse), (layer) => {
        const gradient = layer.createRadialGradient(
          centre,
          centre,
          centre * 0.6,
          centre,
          centre,
          centre,
        );
        gradient.addColorStop(0, tone(colour, 0, 0, 0));
        gradient.addColorStop(
          0.6,
          tone(colour, 0, -5, strength * (0.5 + 0.5 * pulse)),
        );
        gradient.addColorStop(
          1,
          tone(colour, 0, 5, strength * (0.8 + 0.2 * pulse)),
        );
        layer.fillStyle = gradient;
        layer.fillRect(0, 0, size, size);
      });
      break;
    }

    case "lightning": {
      const bolts = many(3);
      for (let i = 0; i < bolts; i++) {
        const start = i / bolts;
        const local = (t - start + 1) % 1;
        const window = 0.16;
        if (local > window) continue;
        const flicker =
          local < window * 0.35 ? 1 : local < window * 0.55 ? 0.35 : 0.85;
        const shape = seeded(100 + i * 7 + (local > window * 0.5 ? 1 : 0));
        const from = seeded(200 + i)() * TAU;
        const to = from + 0.7 + shape() * 0.5;
        context.save();
        context.globalAlpha = flicker;
        context.lineJoin = "round";
        context.lineCap = "round";
        context.strokeStyle = tone(colour, 0, 10, 0.75);
        context.shadowColor = colour;
        context.shadowBlur = size * 0.03;
        context.lineWidth = size * 0.014;
        const r = centre * 0.88;
        const jitter = centre * 0.06;
        const again = seeded(300 + i * 7 + (local > window * 0.5 ? 1 : 0));
        bolt(context, centre, r, from, to, jitter, shape);
        context.shadowBlur = 0;
        context.strokeStyle = "#ffffff";
        context.lineWidth = size * 0.005;
        bolt(context, centre, r, from, to, jitter, again);
        context.restore();
      }
      break;
    }

    case "embers":
      for (let i = 0; i < many(14); i++) {
        const side = i % 2 ? 1 : -1;
        const progress = (t + random()) % 1;
        const inset = 0.78 + random() * 0.18;
        const y = size * (1 - 0.75 * progress);
        const x =
          onBorder(size, side, y, inset) +
          Math.sin(TAU * (t + random())) * size * 0.015;
        const flicker = 0.6 + 0.4 * Math.sin(TAU * (3 * t + random()));
        const radius = size * (0.008 + random() * 0.008);
        context.save();
        context.globalAlpha = Math.sin(Math.PI * progress) * flicker;
        // custom: born in colour 1, turning into colour 2 as it rises
        context.fillStyle = custom
          ? tone(progress < 0.5 ? colour : colour2, 0, 12)
          : progress < 0.5
            ? "#fde047"
            : "#fb923c";
        context.shadowColor = custom ? colour : "#f97316";
        context.shadowBlur = size * 0.02;
        context.beginPath();
        context.arc(x, y, radius, 0, TAU);
        context.fill();
        context.restore();
      }
      break;

    case "confetti": {
      const colours = custom
        ? [colour, colour2, middle, "#ffffff"]
        : [
            colour,
            tone(colour, 120, 5),
            tone(colour, 240, 5),
            "#fde047",
            "#ffffff",
          ];
      for (let i = 0; i < many(16); i++) {
        const side = i % 2 ? 1 : -1;
        const progress = (t + random()) % 1;
        const inset = 0.7 + random() * 0.25;
        const y = size * (-0.03 + 1.06 * progress);
        const x =
          onBorder(size, side, y, inset) +
          Math.sin(TAU * (t + random())) * size * 0.015;
        const spin = random() < 0.5 ? 1 : -1;
        const phase = random();
        context.save();
        context.globalAlpha = Math.min(1, 3 * Math.sin(Math.PI * progress));
        context.translate(x, y);
        context.rotate(TAU * (spin * t + phase));
        context.scale(Math.cos(TAU * (2 * t + phase)), 1);
        context.fillStyle = colours[i % colours.length];
        context.fillRect(
          -size * 0.017,
          -size * 0.008,
          size * 0.034,
          size * 0.016,
        );
        context.restore();
      }
      break;
    }

    case "notes":
      context.save();
      context.fillStyle = colour;
      context.strokeStyle = colour;
      context.shadowColor = "rgba(0, 0, 0, 0.45)";
      context.shadowBlur = size * 0.01;
      for (let i = 0; i < many(5); i++) {
        const side = i % 2 ? 1 : -1;
        const progress = (t + i / many(5)) % 1;
        const inset = 0.8 + random() * 0.08;
        const y = size * (0.92 - 0.7 * progress);
        const x =
          onBorder(size, side, y, inset) +
          Math.sin(TAU * (t + random())) * size * 0.02 -
          size * 0.03;
        context.globalAlpha = Math.sin(Math.PI * progress);
        note(context, x, y, size * 0.075, i % 3 === 1);
      }
      context.restore();
      break;

    case "orbit": {
      const r = centre * 0.84;
      // the path, faint
      context.save();
      context.strokeStyle = tone(colour, 0, 10, 0.3);
      context.lineWidth = size * 0.004;
      context.setLineDash([size * 0.012, size * 0.018]);
      context.beginPath();
      context.arc(centre, centre, r, 0, TAU);
      context.stroke();
      context.setLineDash([]);
      const angle = TAU * t - Math.PI / 2;
      // a fading tail behind the planet
      const tail = context.createConicGradient(angle - 1.2, centre, centre);
      tail.addColorStop(0, tone(colour, 0, 20, 0));
      tail.addColorStop(1.2 / TAU, tone(colour, 0, 20, 0.7));
      tail.addColorStop(1.2 / TAU + 0.001, tone(colour, 0, 20, 0));
      context.strokeStyle = tail;
      context.lineWidth = size * 0.012;
      context.lineCap = "round";
      context.beginPath();
      context.arc(centre, centre, r, angle - 1.2, angle);
      context.stroke();
      const x = centre + Math.cos(angle) * r;
      const y = centre + Math.sin(angle) * r;
      const planet = size * 0.05;
      // ring around the planet, back half
      context.strokeStyle = custom
        ? tone(colour2, 0, 10, 0.8)
        : tone(colour, 40, 25, 0.8);
      context.lineWidth = size * 0.007;
      context.beginPath();
      context.ellipse(x, y, planet * 1.7, planet * 0.5, -0.4, Math.PI, TAU);
      context.stroke();
      const body = context.createRadialGradient(
        x - planet * 0.35,
        y - planet * 0.35,
        planet * 0.1,
        x,
        y,
        planet,
      );
      body.addColorStop(0, tone(colour, 0, 30));
      body.addColorStop(1, tone(colour, 0, -25));
      context.fillStyle = body;
      context.beginPath();
      context.arc(x, y, planet, 0, TAU);
      context.fill();
      // ring, front half
      context.beginPath();
      context.ellipse(x, y, planet * 1.7, planet * 0.5, -0.4, 0, Math.PI);
      context.stroke();
      // a moon going round the planet twice per loop
      const moonAngle = TAU * 2 * t;
      const mx = x + Math.cos(moonAngle) * planet * 2.2;
      const my = y + Math.sin(moonAngle) * planet * 2.2 * 0.6;
      context.fillStyle = custom ? tone(colour2, 0, 15) : "#e2e8f0";
      context.shadowColor = "#ffffff";
      context.shadowBlur = size * 0.01;
      context.beginPath();
      context.arc(mx, my, size * 0.016, 0, TAU);
      context.fill();
      context.restore();
      break;
    }

    case "sweep": {
      const strength = Math.min(1, 0.7 * scale);
      edgeLayer(
        context,
        size,
        0.68,
        (layer) => {
          const gradient = layer.createConicGradient(TAU * t, centre, centre);
          gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
          gradient.addColorStop(0.07, tone(colour, 0, 15, strength * 0.6));
          gradient.addColorStop(0.11, `rgba(255, 255, 255, ${strength})`);
          gradient.addColorStop(0.13, tone(colour, 0, 15, strength * 0.4));
          gradient.addColorStop(0.2, "rgba(255, 255, 255, 0)");
          gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
          layer.fillStyle = gradient;
          layer.fillRect(0, 0, size, size);
        },
        "lighter",
      );
      break;
    }

    case "rain":
      context.save();
      context.lineCap = "round";
      context.lineWidth = size * 0.006;
      for (let i = 0; i < many(22); i++) {
        const x0 = random() * size * 1.2 - size * 0.1;
        const progress = (2 * t + random()) % 1;
        const y = progress * size * 1.2 - size * 0.1;
        const x = x0 - progress * size * 0.08;
        const fade = edgeFade(size, x, y);
        if (fade <= 0) continue;
        context.strokeStyle = custom
          ? tone(i % 2 ? colour2 : colour, 0, 20, 0.85 * fade)
          : `rgba(191, 219, 254, ${0.85 * fade})`;
        context.beginPath();
        context.moveTo(x, y);
        context.lineTo(x + size * 0.012, y - size * 0.05);
        context.stroke();
      }
      context.restore();
      break;

    case "galaxy": {
      // a soft nebula that stays still (costs nothing in the GIF)
      edgeLayer(context, size, 0.66, (layer) => {
        const haze = layer.createConicGradient(0.6, centre, centre);
        haze.addColorStop(0, tone(colour, 0, 5, 0.35));
        haze.addColorStop(
          0.33,
          custom ? tone(middle, 0, 0, 0.15) : tone(colour, 60, 0, 0.15),
        );
        haze.addColorStop(
          0.66,
          custom ? tone(colour2, 0, 5, 0.35) : tone(colour, -50, 5, 0.35),
        );
        haze.addColorStop(1, tone(colour, 0, 5, 0.35));
        layer.fillStyle = haze;
        layer.fillRect(0, 0, size, size);
      });
      // the stars repeat 8 times around, so turning 1/8 per loop is seamless
      const sectors = 8;
      const stars = many(9);
      const placed = Array.from({ length: stars }, () => ({
        angle: (random() * TAU) / sectors,
        distance: centre * (0.72 + random() * 0.26),
        radius: size * (0.003 + random() * 0.006),
        phase: random(),
      }));
      context.save();
      for (let s = 0; s < sectors; s++) {
        for (const star of placed) {
          const angle = star.angle + (s * TAU) / sectors + (t * TAU) / sectors;
          // after one loop each star sits where its neighbour was: its
          // twinkle has to line up with the neighbour's too (3/8 apart)
          const twinkle =
            0.45 +
            0.55 *
              (0.5 +
                0.5 *
                  Math.sin(TAU * (star.phase + (s * 3) / 8 + (t * 11) / 8)));
          const x = centre + Math.cos(angle) * star.distance;
          const y = centre + Math.sin(angle) * star.distance;
          context.fillStyle = `rgba(255, 255, 255, ${twinkle})`;
          context.beginPath();
          context.arc(x, y, star.radius, 0, TAU);
          context.fill();
        }
      }
      context.restore();
      break;
    }
  }
}

/**
 * One frame: the picture, then the effect, then the frame on top
 */
export function drawDecorated(
  context: CanvasRenderingContext2D,
  image: CanvasImageSource,
  size: number,
  t: number,
  decoration: Decoration,
) {
  context.clearRect(0, 0, size, size);
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, size, size);
  drawEffect(context, size, t, decoration);
  const blur = GLOWS.find((glow) => glow.id === decoration.glow)?.blur ?? 0;
  if (!blur || decoration.ring === "none") {
    drawRing(context, size, t, decoration);
    return;
  }
  // Konkord: the frame on its own, drawn with a light around whatever its shape
  ringLayer ??= document.createElement("canvas");
  if (ringLayer.width !== size) ringLayer.width = ringLayer.height = size;
  const layer = ringLayer.getContext("2d")!;
  layer.clearRect(0, 0, size, size);
  drawRing(layer, size, t, decoration);
  context.save();
  context.shadowColor = decoration.colour;
  context.shadowBlur = size * blur;
  context.drawImage(ringLayer, 0, 0);
  context.restore();
}

let ringLayer: HTMLCanvasElement | undefined;
